import { addDays, includesMember, type Region } from '@shh/shared';
import { Agenda, Heading, Text, TextField } from '@shh/ui';
import { agendaItems, dayLabel } from '../../data/agenda';
import { occursOn, type E } from '../../data/derive';
import type { FamilyData } from '../../data/family';
import { calendarText } from '../../i18n/calendar';

const fold = (s: string) =>
  s.toLowerCase().replace(/ß/g, 'ss').normalize('NFD').replace(/[̀-ͯ]/g, '');

/** Next day a (series) event happens on, from `from` on – within a year. */
function nextDay(e: E, from: string, region?: Region): string | undefined {
  if (!e.repeat) return e.date;
  for (let d = e.date > from ? e.date : from, i = 0; i < 366; i++, d = addDays(d, 1)) {
    if (occursOn(e, d, region)) return d;
  }
  return undefined;
}

/** Hits for a search: title, place or person (umlauts and case don't matter). */
export function searchEvents(f: FamilyData, q: string, people?: string[]) {
  const needle = fold(q.trim());
  if (!needle) return { upcoming: [], earlier: [] };
  const names = (e: E) =>
    e.assignees === 'all'
      ? ''
      : f.members
          .filter((m) => includesMember(e.assignees, m.id))
          .map((m) => m.name)
          .join(' ');
  const hits = (f.events as E[])
    .filter((e) => e.source !== 'waste')
    .filter(
      (e) => !people || e.assignees === 'all' || people.some((p) => includesMember(e.assignees, p)),
    )
    .filter((e) => fold(`${e.title} ${e.location ?? ''} ${names(e)}`).includes(needle))
    .map((e) => ({ e, date: nextDay(e, f.today, f.household?.location?.region) }))
    .filter((h): h is { e: E; date: string } => !!h.date);
  const upcoming = hits
    .filter((h) => h.date >= f.today)
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(0, 40);
  const earlier = hits
    .filter((h) => h.date < f.today)
    .sort((a, b) => b.date.localeCompare(a.date))
    .slice(0, 10);
  return { upcoming, earlier };
}

export function CalendarSearch({
  f,
  query,
  onQuery,
  people,
  now,
  lens,
  onOpen,
  field = true,
}: {
  f: FamilyData;
  query: string;
  onQuery: (q: string) => void;
  people?: string[];
  now: string;
  lens: string;
  onOpen: (id: string, date: string) => void;
  /** The phone shows its own field; big screens search from the sidebar. */
  field?: boolean;
}) {
  const { upcoming, earlier } = searchEvents(f, query, people);
  const group = (list: { e: E; date: string }[]) => {
    const byDay = new Map<string, E[]>();
    for (const h of list)
      byDay.set(h.date, [...(byDay.get(h.date) ?? []), { ...h.e, date: h.date }]);
    return [...byDay].map(([d, es]) => {
      const info = dayLabel(f, d, true);
      return (
        <section
          key={d}
          aria-label={`${info.label}, ${info.date}`}
          className="flex flex-col gap-gap-sm"
        >
          <h3 className="font-display text-lg font-black">
            {info.label}, {info.date}
          </h3>
          <Agenda
            items={agendaItems(f, d, now, lens, () => null, { list: es }).map((i) => ({
              ...i,
              dimmed: false,
            }))}
            onItemPress={(id) => onOpen(id, d)}
          />
        </section>
      );
    });
  };
  return (
    <div className="flex flex-col gap-section" role="search">
      {field && (
        <TextField
          label={calendarText.searchLabel}
          value={query}
          onChange={onQuery}
          autoFocus
          type="search"
        />
      )}
      {!query.trim() ? (
        <Text tone="secondary">{calendarText.searchHint}</Text>
      ) : !upcoming.length && !earlier.length ? (
        <Text tone="secondary">{calendarText.noHits(query.trim())}</Text>
      ) : (
        <>
          {upcoming.length > 0 && (
            <div className="flex flex-col gap-gap-md">
              <Heading level={2} size="md">
                {calendarText.upcoming}
              </Heading>
              {group(upcoming)}
            </div>
          )}
          {earlier.length > 0 && (
            <div className="flex flex-col gap-gap-md">
              <Heading level={2} size="md">
                {calendarText.earlier}
              </Heading>
              {group(earlier)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
